import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const UpazilaAdminSettings = () => {
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: false,
      sms: true
    },
    security: {
      twoFactorAuth: true,
      sessionTimeout: 30,
      passwordExpiry: 90
    },
    preferences: {
      language: 'bn',
      theme: 'light',
      autoSave: true
    }
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const handleSave = () => {
    // In a real app, this would save to backend
    setSnackbar({
      open: true,
      message: 'সেটিংস সফলভাবে সংরক্ষিত হয়েছে',
      severity: 'success'
    });
  };

  const handleReset = () => {
    // Reset to default settings
    setSnackbar({
      open: true,
      message: 'সেটিংস পুনরায় সেট করা হয়েছে',
      severity: 'info'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        উপজেলা প্রশাসক সেটিংস
      </Typography>

      <Grid container spacing={3}>
        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                বিজ্ঞপ্তি সেটিংস
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="ইমেইল বিজ্ঞপ্তি"
                  secondary="ইমেইলের মাধ্যমে বিজ্ঞপ্তি পান"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.notifications.email}
                    onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="পুশ বিজ্ঞপ্তি"
                  secondary="ব্রাউজারে পুশ বিজ্ঞপ্তি পান"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.notifications.push}
                    onChange={(e) => handleSettingChange('notifications', 'push', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="এসএমএস বিজ্ঞপ্তি"
                  secondary="মোবাইলে এসএমএস বিজ্ঞপ্তি পান"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.notifications.sms}
                    onChange={(e) => handleSettingChange('notifications', 'sms', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                নিরাপত্তা সেটিংস
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="দুই-ফ্যাক্টর প্রমাণীকরণ"
                  secondary="অতিরিক্ত নিরাপত্তার জন্য"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.security.twoFactorAuth}
                    onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="সেশন টাইমআউট"
                  secondary="মিনিটে (নিষ্ক্রিয় থাকলে)"
                />
                <ListItemSecondaryAction>
                  <TextField
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ min: 5, max: 120 }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="পাসওয়ার্ড মেয়াদ"
                  secondary="দিনে (পরিবর্তন করতে হবে)"
                />
                <ListItemSecondaryAction>
                  <TextField
                    type="number"
                    value={settings.security.passwordExpiry}
                    onChange={(e) => handleSettingChange('security', 'passwordExpiry', parseInt(e.target.value))}
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ min: 30, max: 365 }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LanguageIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                পছন্দসমূহ
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="ভাষা"
                  secondary="ইন্টারফেস ভাষা নির্বাচন করুন"
                />
                <ListItemSecondaryAction>
                  <TextField
                    select
                    value={settings.preferences.language}
                    onChange={(e) => handleSettingChange('preferences', 'language', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="bn">বাংলা</option>
                    <option value="en">English</option>
                  </TextField>
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="থিম"
                  secondary="ডার্ক বা লাইট থিম"
                />
                <ListItemSecondaryAction>
                  <TextField
                    select
                    value={settings.preferences.theme}
                    onChange={(e) => handleSettingChange('preferences', 'theme', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="light">লাইট</option>
                    <option value="dark">ডার্ক</option>
                  </TextField>
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText 
                  primary="স্বয়ংক্রিয় সংরক্ষণ"
                  secondary="পরিবর্তনগুলি স্বয়ংক্রিয়ভাবে সংরক্ষণ করুন"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={settings.preferences.autoSave}
                    onChange={(e) => handleSettingChange('preferences', 'autoSave', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                কার্যক্রম
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                fullWidth
                size="large"
              >
                সেটিংস সংরক্ষণ করুন
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                fullWidth
                size="large"
              >
                ডিফল্ট সেটিংস
              </Button>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              সেটিংস পরিবর্তন করার পর সংরক্ষণ করতে ভুলবেন না
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpazilaAdminSettings;
